import { Injectable } from '@angular/core';

import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { OrganizationDto, OrganizationGroupDto, ProductLastSearchesDto, SubmissionsLastSearchesDto } from '@scip/api';
import { OrganizationControllerService, SearchControllerService } from '@scip/api';
import { OrganizationGroupService } from './organization-group.service';
import { ProfileService } from './profile.service';
import { TranslateService } from '@ngx-translate/core';
import { translationKeys } from '../core';

@Injectable({
  providedIn: 'root'
})
export class RecentSearchService {
  //translation keys
  private i18nProductName = translationKeys.recentSearches.component.searchLabel.productName;
  private i18nProductNumber = translationKeys.recentSearches.component.searchLabel.productNumber;
  private i18nParentDetails = translationKeys.recentSearches.component.searchLabel.parentDetails;
  private i18nRequestedType = translationKeys.recentSearches.component.searchLabel.requestedType;
  private i18nShowChildren = translationKeys.recentSearches.component.searchLabel.showChildren;
  private i18nOrganizations = translationKeys.recentSearches.component.searchLabel.organizations;
  private i18nOrganizationGroups = translationKeys.recentSearches.component.searchLabel.organizationGroups;
  private i18nEchaType = translationKeys.recentSearches.component.searchLabel.echaType;
  private i18nSubmissionNumber = translationKeys.recentSearches.component.searchLabel.submissionNumber;
  private i18nScipNumber = translationKeys.recentSearches.component.searchLabel.scipNumber;
  private i18nUploadDateFrom = translationKeys.recentSearches.component.searchLabel.uploadDateFrom;
  private i18nUploadDateTo = translationKeys.recentSearches.component.searchLabel.uploadDateTo;
  private i18nEmptyAdvancedSearch = translationKeys.recentSearches.component.searchLabel.emptyAdvancedSearch;
  private i18nSubmissionDateFrom = translationKeys.recentSearches.component.searchLabel.submissionDateFrom;
  private i18nSubmissionDateTo = translationKeys.recentSearches.component.searchLabel.submissionDateTo;
  private i18nSubmissionStatus = translationKeys.recentSearches.component.searchLabel.submissionStatuses;
  private i18nScipRelevantChangedFrom = translationKeys.recentSearches.component.searchLabel.scipRelevantChangedFrom;
  private i18nScipRelevantChangedTo = translationKeys.recentSearches.component.searchLabel.scipRelevantChangedTo;
  private i18nEuMarketRelevantChangedFrom = translationKeys.recentSearches.component.searchLabel.euMarketRelevantChangedFrom;
  private i18nEuMarketRelevantChangedTo = translationKeys.recentSearches.component.searchLabel.euMarketRelevantChangedTo;
  private i18nProductLoadingState = translationKeys.recentSearches.component.searchLabel.productLoadingState;

  constructor(
    private searchControllerService: SearchControllerService,
    private translateService: TranslateService,
    private organizationControllerService: OrganizationControllerService,
    private organizationGroupService: OrganizationGroupService,
    private profileService: ProfileService
  ) {}

  getLastTenSearches(type: RecentSearchTypeEnum, includeOrganizationGrouping = false): Observable<RecentSearch[]> {
    let getAllOrganizationGroups: Observable<OrganizationGroupDto[]>;
    if (includeOrganizationGrouping) {
      getAllOrganizationGroups = this.organizationGroupService.getAllOrganizationGroups(true);
    } else {
      getAllOrganizationGroups = of([]);
    }

    if (type === RecentSearchTypeEnum.Products) {
      // Load last searches as well as organization list. Organization list is needed for translating orgIds to org names.
      return combineLatest([
        this.searchControllerService.getProductLastSearches(),
        this.profileService.getAllOrganizations(true),
        getAllOrganizationGroups
      ]).pipe(
        map(([data, allOrgs, allOrgGroups]: [ProductLastSearchesDto[], OrganizationDto[], OrganizationGroupDto[]]) => {
          return this.createRecentProductSearchesLabelText(data, allOrgs, allOrgGroups);
        }),
        catchError(() => of([]))
      );
    }
    if (type === RecentSearchTypeEnum.Submissions) {
      return combineLatest([
        this.searchControllerService.getSubmissionsLastSearches(),
        this.profileService.getAllOrganizations(true),
        getAllOrganizationGroups
      ]).pipe(
        map(([data, allOrgs, allOrgGroups]: [SubmissionsLastSearchesDto[], OrganizationDto[], OrganizationGroupDto[]]) => {
          return this.createRecentSubmissionSearchesLabelText(data, allOrgs, allOrgGroups);
        }),
        catchError(() => of([]))
      );
    }
  }

  private createRecentProductSearchesLabelText(
    searches: ProductLastSearchesDto[],
    allOrgs: OrganizationDto[],
    allOrgGroups: OrganizationGroupDto[]
  ): RecentSearch[] {
    return searches.map((search: ProductLastSearchesDto) => {
      return {
        text: this.generateProductSearchLabelText(search, allOrgs, allOrgGroups),
        savedHtml: this.generateProductSearchLabelText(search, allOrgs, allOrgGroups),
        productLastSearchesDto: search
      };
    });
  }

  private createRecentSubmissionSearchesLabelText(
    searches: SubmissionsLastSearchesDto[],
    allOrgs: OrganizationDto[],
    allOrgGroups: OrganizationGroupDto[]
  ): RecentSearch[] {
    return searches.map((search: SubmissionsLastSearchesDto) => {
      return {
        text: this.generateSubmissionSearchLabelText(search, allOrgs, allOrgGroups),
        savedHtml: this.generateSubmissionSearchLabelText(search, allOrgs, allOrgGroups),
        submissionSearchesDto: search
      };
    });
  }

  /**
   * Appends all available values of a ProductLastSearchesDto into a comma separated string
   * @returns {label} Returns the comma separated string.
   */
  private generateProductSearchLabelText(pDto: ProductLastSearchesDto, allOrgs: OrganizationDto[], allOrgGroups: OrganizationGroupDto[]): string {
    if (pDto.searchObject) {
      return pDto.searchObject;
    } else if (pDto.searchDto) {
      const values = this.translateService.instant([
        this.i18nProductName,
        this.i18nProductNumber,
        this.i18nRequestedType,
        this.i18nOrganizations,
        this.i18nUploadDateFrom,
        this.i18nUploadDateTo,
        this.i18nEmptyAdvancedSearch,
        this.i18nOrganizationGroups,
        this.i18nScipRelevantChangedFrom,
        this.i18nScipRelevantChangedTo,
        this.i18nEuMarketRelevantChangedFrom,
        this.i18nEuMarketRelevantChangedTo,
        this.i18nProductLoadingState
      ]);
      const label: string[] = [];
      if (pDto.searchDto.productName?.length) {
        label.push(values[this.i18nProductName] + pDto.searchDto.productName);
      }
      if (pDto.searchDto.productNumber?.length) {
        label.push(values[this.i18nProductNumber] + pDto.searchDto.productNumber);
      }
      if (pDto.searchDto.requestedType?.length) {
        label.push(values[this.i18nRequestedType] + pDto.searchDto.requestedType);
      }
      if (pDto.searchDto.organizationsIds?.length) {
        const orgNames = this.translateOrgIdsToOrgNames(pDto.searchDto.organizationsIds, allOrgs);
        label.push(values[this.i18nOrganizations] + orgNames);
      }
      if (pDto.searchDto.organizationGroupIds?.length) {
        const orgGroupNames = this.translateOrgGroupIdsToNames(pDto.searchDto.organizationGroupIds, allOrgGroups);
        label.push(values[this.i18nOrganizationGroups] + orgGroupNames);
      }
      if (pDto.searchDto.lastUpdatedFrom?.length) {
        label.push(values[this.i18nUploadDateFrom] + pDto.searchDto.lastUpdatedFrom);
      }
      if (pDto.searchDto.lastUpdatedTo?.length) {
        label.push(values[this.i18nUploadDateTo] + pDto.searchDto.lastUpdatedTo);
      }
      if (pDto.searchDto.submissionDateFrom?.length) {
        label.push(values[this.i18nSubmissionDateFrom] + pDto.searchDto.submissionDateFrom);
      }
      if (pDto.searchDto.submissionDateTo?.length) {
        label.push(values[this.i18nSubmissionDateTo] + pDto.searchDto.submissionDateTo);
      }
      if (pDto.searchDto.scipRelevantChangedFrom?.length) {
        label.push(values[this.i18nScipRelevantChangedFrom] + pDto.searchDto.scipRelevantChangedFrom);
      }
      if (pDto.searchDto.scipRelevantChangedTo?.length) {
        label.push(values[this.i18nScipRelevantChangedTo] + pDto.searchDto.scipRelevantChangedTo);
      }
      if (pDto.searchDto.euMarketRelevantChangedFrom?.length) {
        label.push(values[this.i18nEuMarketRelevantChangedFrom] + pDto.searchDto.euMarketRelevantChangedFrom);
      }
      if (pDto.searchDto.euMarketRelevantChangedTo?.length) {
        label.push(values[this.i18nEuMarketRelevantChangedTo] + pDto.searchDto.euMarketRelevantChangedTo);
      }
      if (pDto.searchDto.productLoadingState) {
        label.push(values[this.i18nProductLoadingState] + pDto.searchDto.productLoadingState);
      }

      if (label.length) {
        return label.join(', ');
      } else {
        return values[this.i18nEmptyAdvancedSearch];
      }
    }
  }

  /**
   * Appends all available values of a ProductLastSearchesDto into a comma separated string
   * @returns {label} Returns the comma separated string.
   */
  private generateSubmissionSearchLabelText(sDto: SubmissionsLastSearchesDto, allOrgs: OrganizationDto[], allOrgGroups: OrganizationGroupDto[]): string {
    if (sDto.searchObject) {
      return sDto.searchObject;
    } else if (sDto.searchDto) {
      const values = this.translateService.instant([
        this.i18nProductName,
        this.i18nProductNumber,
        this.i18nParentDetails,
        this.i18nRequestedType,
        this.i18nShowChildren,
        this.i18nOrganizations,
        this.i18nEchaType,
        this.i18nSubmissionNumber,
        this.i18nScipNumber,
        this.i18nUploadDateFrom,
        this.i18nUploadDateTo,
        this.i18nSubmissionDateFrom,
        this.i18nSubmissionDateTo,
        this.i18nSubmissionStatus,
        this.i18nEmptyAdvancedSearch,
        this.i18nOrganizationGroups
      ]);
      const label: string[] = [];
      if (sDto.searchDto.productName?.length) {
        label.push(values[this.i18nProductName] + sDto.searchDto.productName);
      }
      if (sDto.searchDto.productNumber?.length) {
        label.push(values[this.i18nProductNumber] + sDto.searchDto.productNumber);
      }
      if (sDto.searchDto.parentDetails?.length) {
        label.push(values[this.i18nParentDetails] + sDto.searchDto.parentDetails);
      }
      if (sDto.searchDto.requestedType?.length) {
        label.push(values[this.i18nRequestedType] + sDto.searchDto.requestedType);
      }
      if (sDto.searchDto.organizationsIds?.length) {
        const orgNames = this.translateOrgIdsToOrgNames(sDto.searchDto.organizationsIds, allOrgs);
        label.push(values[this.i18nOrganizations] + orgNames);
      }
      if (sDto.searchDto.organizationGroupIds?.length) {
        const orgGroupNames = this.translateOrgGroupIdsToNames(sDto.searchDto.organizationGroupIds, allOrgGroups);
        label.push(values[this.i18nOrganizationGroups] + orgGroupNames);
      }
      if (sDto.searchDto.echaDossierType?.length) {
        label.push(values[this.i18nEchaType] + sDto.searchDto.echaDossierType);
      }
      if (sDto.searchDto.submissionNumber?.length) {
        label.push(values[this.i18nSubmissionNumber] + sDto.searchDto.submissionNumber);
      }
      if (sDto.searchDto.scipNumber?.length) {
        label.push(values[this.i18nScipNumber] + sDto.searchDto.scipNumber);
      }
      if (sDto.searchDto.submissionDateFrom?.length) {
        label.push(values[this.i18nSubmissionDateFrom] + sDto.searchDto.submissionDateFrom);
      }
      if (sDto.searchDto.submissionDateTo?.length) {
        label.push(values[this.i18nSubmissionDateTo] + sDto.searchDto.submissionDateTo);
      }
      if (sDto.searchDto.submissionStatuses?.length) {
        label.push(values[this.i18nSubmissionStatus] + sDto.searchDto.submissionStatuses);
      }

      if (label.length) {
        return label.join(', ');
      } else {
        return values[this.i18nEmptyAdvancedSearch];
      }
    }
  }

  private translateOrgIdsToOrgNames(orgIds: string[], organizationsList: OrganizationDto[]): string[] {
    return organizationsList.filter((o) => orgIds.find((id) => id === o.identifier)).map((o) => o.name);
  }

  private translateOrgGroupIdsToNames(orgIds: string[], organizationGroupList: OrganizationGroupDto[]): string[] {
    return organizationGroupList.filter((o) => orgIds.find((id) => id === o.identifier)).map((o) => o.name);
  }
}

export interface RecentSearch {
  text: string;
  savedHtml: string;
  productLastSearchesDto?: ProductLastSearchesDto;
  submissionSearchesDto?: SubmissionsLastSearchesDto;
}

export enum RecentSearchTypeEnum {
  Products,
  Submissions
}
